import { useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { ApiError } from '../api.js';
import { texts, useLang } from '../i18n.js';
import { toast } from '../ui.js';
import { Tip, TIPS } from '../guide.js';

/**
 * A new version from the crew's own link (Andreas 2026-10-18): the freelance editor picks the file on their computer,
 * the Hub puts it in the project's Drive folder and sends it for review like the Files tab does for staff.
 */

const TX = texts(
  {
    title: 'Send a new version',
    info: 'The file goes into the project’s folder in Drive and to the CD and the producer first. Export an MP4 (H.264).',
    draft: 'Draft',
    final: 'Final formats',
    pick: 'Choose file',
    pickMany: 'Choose files',
    send: 'Upload and send',
    retry: 'Try sending again',
    uploading: (name: string, pct: number) => `Uploading ${name} – ${pct} %`,
    sending: 'Sending…',
    sent: (n: number) => `Version ${n} is sent for review.`
  },
  {
    title: 'Skicka en ny version',
    info: 'Filen hamnar i projektets mapp i Drive och går först till CD och producenten. Exportera en MP4 (H.264).',
    draft: 'Utkast',
    final: 'Slutformat',
    pick: 'Välj fil',
    pickMany: 'Välj filer',
    send: 'Ladda upp och skicka',
    retry: 'Försök skicka igen',
    uploading: (name: string, pct: number) => `Laddar upp ${name} – ${pct} %`,
    sending: 'Skickar…',
    sent: (n: number) => `Version ${n} är skickad för granskning.`
  }
);

type Step = { done: false; received: number } | { done: true; file: { id: string; name: string; ticket: string } };

/** Sends one file in chunks to the Hub, which passes each to Drive; returns the ticket to send it with. */
async function upload(api: Api, base: string, videoId: string, file: File, progress: (sent: number) => void): Promise<string> {
  const s = await api.post<{ token: string; chunk: number }>(`${base}/review/upload`, { videoId, name: file.name, size: file.size, mimeType: file.type || undefined });
  let offset = 0;
  for (;;) {
    const q = new URLSearchParams({ t: s.token, offset: String(offset) });
    const step = await api.putBytes<Step>(`${base}/review/upload?${q}`, file.slice(offset, Math.min(offset + s.chunk, file.size)));
    if (step.done) return step.file.ticket;
    // Google says how much it holds; carry on from there
    offset = step.received;
    progress(offset);
  }
}

export function CrewSend(p: { api: Api; base: string; videoId: string; onSent: (versionId: string) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [kind, setKind] = useState<'draft' | 'final'>('draft');
  const [files, setFiles] = useState<File[]>([]);
  const [tickets, setTickets] = useState<string[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const run = async () => {
    try {
      let ts = tickets;
      if (!ts) {
        ts = [];
        for (const f of files) {
          setBusy(t.uploading(f.name, 0));
          ts.push(await upload(p.api, p.base, p.videoId, f, (sent) => setBusy(t.uploading(f.name, Math.round((sent / f.size) * 100)))));
        }
        // uploaded: a failed send is tried again without uploading again
        setTickets(ts);
      }
      setBusy(t.sending);
      const r = await p.api.post<{ versionId: string; number: number }>(`${p.base}/review/send`, { videoId: p.videoId, kind, files: ts.map((ticket) => ({ ticket })) });
      toast({ text: t.sent(r.number) });
      setFiles([]);
      setTickets(null);
      p.onSent(r.versionId);
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <section class="rv-decide" aria-label={t.title} data-tour="send-version">
      <h2 class="rv-h2">{t.title}</h2>
      <p class="rv-meta">{t.info}</p>
      <Tip id="naming" title={TIPS[lang].namingTitle}>
        {TIPS[lang].naming}
      </Tip>
      <div class="rv-row">
        <select class="input" aria-label={t.title} value={kind} disabled={Boolean(busy) || Boolean(tickets)} onChange={(e) => (setKind((e.currentTarget as HTMLSelectElement).value as 'draft' | 'final'), setFiles([]))}>
          <option value="draft">{t.draft}</option>
          <option value="final">{t.final}</option>
        </select>
        <label class={`btn${busy || tickets ? ' is-disabled' : ''}`}>
          {kind === 'final' ? t.pickMany : t.pick}
          <input
            type="file"
            accept="video/*,.mp4,.mov,.m4v"
            multiple={kind === 'final'}
            hidden
            disabled={Boolean(busy) || Boolean(tickets)}
            onChange={(e) => setFiles([...((e.currentTarget as HTMLInputElement).files ?? [])])}
          />
        </label>
        <button type="button" class="btn btn-primary" disabled={Boolean(busy) || !files.length} onClick={() => void run()}>
          {tickets ? t.retry : t.send}
        </button>
      </div>
      {files.length ? <p class="rv-meta">{files.map((f) => f.name).join(', ')}</p> : null}
      {busy ? <p class="rv-meta" role="status">{busy}</p> : null}
    </section>
  );
}
